import type { LucideIcon } from 'lucide-react'

import type { PageIconTone } from '@/shared/components/PageIconBadge'
import {
  BadgeCheck,
  Coins,
  Copyright,
  HardDrive,
  LayoutDashboard,
  MessageSquarePlus,
  Shield,
  UserCircle,
  Wallet,
} from 'lucide-react'

import type { TenantFeatureKey } from './tenant-features'

export interface PublicNavItem {
  to: string
  i18nKey: string
  end?: boolean
  /** 顶栏高亮：路径前缀匹配 */
  matchPrefix?: string
  /** 门户显隐键(如 nav.home / nav.works) */
  portalKey?: string
  /** SaaS 功能模块;未开通则不显示 */
  feature?: TenantFeatureKey
  /** 标记需登录才能使用完整功能；导航仍直达目标页，由页面内统一展示登录引导 */
  requiresAuth?: boolean
}

export const SUBSCRIPTION_NAV_ITEM: PublicNavItem = {
  to: '/subscription',
  i18nKey: 'nav.subscription',
  matchPrefix: '/subscription',
  feature: 'workbench',
}

/** 创作中心 in-app 入口 */
export const STUDIO_NAV_ITEM: PublicNavItem = {
  to: '/studio',
  i18nKey: 'nav.studio',
  matchPrefix: '/studio',
  feature: 'workbench',
  requiresAuth: true,
}

/** 顶栏公开导航(无需登录) */
export const PUBLIC_NAV_ITEMS: PublicNavItem[] = [
  { to: '/', i18nKey: 'nav.home', end: true, portalKey: 'nav.home' },
  { to: '/works', i18nKey: 'nav.works', portalKey: 'nav.works' },
  { to: '/contests', i18nKey: 'nav.contests', feature: 'contests' },
  { to: '/marketplace', i18nKey: 'nav.marketplace', feature: 'marketplace' },
  { to: '/tasks', i18nKey: 'nav.tasks', feature: 'task_center' },
  { to: '/learning', i18nKey: 'nav.learning', feature: 'learning_center' },
]

/** 平台总门户顶栏 — 固定展示四模块 catalog */
export const PLATFORM_PUBLIC_NAV_ITEMS: PublicNavItem[] = [
  { to: '/', i18nKey: 'nav.home', end: true, portalKey: 'nav.home' },
  { to: '/works', i18nKey: 'nav.works', portalKey: 'nav.works' },
  { to: '/contests', i18nKey: 'nav.contests' },
  { to: '/marketplace', i18nKey: 'nav.marketplace' },
  { to: '/tasks', i18nKey: 'nav.tasks' },
]

/** 顶栏常驻用户入口(始终展示;未登录直达目标页并展示统一登录引导) */
export const AUTHENTICATED_NAV_ITEMS: PublicNavItem[] = [
  STUDIO_NAV_ITEM,
  {
    to: '/account/dashboard',
    i18nKey: 'userMenu.title',
    matchPrefix: '/account',
    requiresAuth: true,
  },
]

/** 导航/菜单项是否可见(租户模块 + 门户开关) */
export function isNavItemVisible(
  item: { feature?: TenantFeatureKey; portalKey?: string },
  isFeatureEnabled: (key: TenantFeatureKey) => boolean,
  isPortalEnabled: (key: string) => boolean = () => true,
): boolean {
  if (item.portalKey && !isPortalEnabled(item.portalKey)) return false
  if (item.feature && !isFeatureEnabled(item.feature)) return false
  return true
}

/** 首个可见的公开导航路径(首页关闭时作 fallback) */
export function firstVisiblePublicNavPath(
  items: PublicNavItem[],
  isFeatureEnabled: (key: TenantFeatureKey) => boolean,
  isPortalEnabled: (key: string) => boolean,
): string {
  for (const item of items) {
    if (isNavItemVisible(item, isFeatureEnabled, isPortalEnabled)) {
      return item.to
    }
  }
  return '/auth/login'
}

/** 顶栏链接：始终直达目标路由，登录校验由目标页负责 */
export function resolveNavItemHref(item: PublicNavItem): string {
  return item.to
}

export function isNavItemActive(pathname: string, item: PublicNavItem): boolean {
  if (item.matchPrefix) {
    return pathname === item.matchPrefix || pathname.startsWith(`${item.matchPrefix}/`)
  }
  if (item.end) return pathname === item.to
  return pathname === item.to || pathname.startsWith(`${item.to}/`)
}

export interface UserMenuItem {
  to: string
  i18nKey: string
  icon: LucideIcon
  tone: PageIconTone
  /** 侧栏高亮:路径前缀匹配 */
  matchPrefix?: string
  feature?: TenantFeatureKey
}

export interface UserMenuGroup {
  i18nKey: string
  items: UserMenuItem[]
}

/** 个人中心侧栏 — 仅 /account/* 页面；会员 / 版权见顶栏或头像菜单 */
export const ACCOUNT_SIDEBAR_GROUPS: UserMenuGroup[] = [
  {
    i18nKey: 'userMenu.groups.center',
    items: [
      { to: '/account/dashboard', i18nKey: 'userMenu.dashboard', icon: LayoutDashboard, tone: 'indigo' },
      { to: '/account/profile', i18nKey: 'userMenu.publicProfile', icon: UserCircle, tone: 'sky' },
      { to: '/account/security', i18nKey: 'userMenu.security', icon: Shield, tone: 'amber' },
      { to: '/account/feedback', i18nKey: 'userMenu.feedback', icon: MessageSquarePlus, tone: 'purple', matchPrefix: '/account/feedback' },
    ],
  },
  {
    i18nKey: 'userMenu.groups.manage',
    items: [
      {
        to: '/account/works',
        i18nKey: 'userMenu.myWorks',
        icon: BadgeCheck,
        tone: 'indigo',
        matchPrefix: '/account/works',
      },
      {
        to: '/account/revenue',
        i18nKey: 'userMenu.revenue',
        icon: Coins,
        tone: 'emerald',
        matchPrefix: '/account/revenue',
      },
      {
        to: '/account/recharge',
        i18nKey: 'userMenu.recharge',
        icon: Wallet,
        tone: 'amber',
        matchPrefix: '/account/recharge',
      },
      {
        to: '/account/storage',
        i18nKey: 'userMenu.storage',
        icon: HardDrive,
        tone: 'sky',
        matchPrefix: '/account/storage',
        feature: 'workbench',
      },
    ],
  },
]

/** 头像下拉额外入口 — 顶栏未展示的模块页 */
export const USER_MENU_EXTRA_GROUPS: UserMenuGroup[] = [
  {
    i18nKey: 'userMenu.groups.services',
    items: [
      {
        to: '/copyright',
        i18nKey: 'userMenu.copyright',
        icon: Copyright,
        tone: 'purple',
        matchPrefix: '/copyright',
        feature: 'copyright',
      },
    ],
  },
]

/** 头像下拉完整菜单 */
export const USER_MENU_GROUPS: UserMenuGroup[] = [
  ...ACCOUNT_SIDEBAR_GROUPS,
  ...USER_MENU_EXTRA_GROUPS,
]

export const USER_MENU_FLAT = USER_MENU_GROUPS.flatMap((g) => g.items)

export function isUserMenuActive(pathname: string, item: UserMenuItem): boolean {
  if (pathname === item.to) return true
  if (item.matchPrefix === '/account/revenue') {
    return pathname.startsWith('/account/revenue') || pathname.startsWith('/account/withdrawals')
  }
  if (item.matchPrefix && pathname.startsWith(item.matchPrefix)) return true
  return false
}
